import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { InfoRounded } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import PropTypes from 'prop-types';

import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { GT_ZERO, INTEGER, REQUIRED } from 'utils/helpers/validationMessages';
import {
  useGetLocationsQuery,
  useGetRecipientsQuery,
  useGetRestrictedLocationsQuery,
} from '../batchesEndpoints';
import RestrictedLocationsList from '../DistributionsDrawer/RestrictedLocationsList/RestrictedLocationsList';
import DataDisplayItem from '../components/DataDisplayItem';

const reconsignmentTooltip = (
  <>
    A Reconsignment Control maximizes efficiencies by limiting loading of transferred barrels to the
    selected terminal location or a location further from the origin.
  </>
);

const totalDistributedVolumeTooltip = (
  <>Total Distributed Volume equals the sum of all associated distribution volumes.</>
);

const EditDistribution = ({
  distributionData,
  editState,
  isDistributionSuccess,
  setEditState,
  setValidationMessages,
  validationMessages,
  isFieldsDisabled = false,
}) => {
  const {
    bookingOriginId,
    dataCompanyId,
    inventoryOwnerId,
    productId,
    locationId,
    shipmentId,
    shipmentVolume,
    totalDistributedVol,
    volume,
  } = distributionData;

  const {
    data: locationsQuery = [],
    isFetching: isFetchingLocations,
    isSuccess: isLocationSuccess,
  } = useGetLocationsQuery(
    {
      params: {
        bookingOriginId,
        currentLocationId: locationId,
        dataCompanyId,
        inventoryOwner: {
          companyCustomerIdName: inventoryOwnerId,
          dataCompanyId,
        },
        productId,
        shipmentId,
      },
    },
    { skip: !isDistributionSuccess }
  );

  const { data: restrictedLocationsQuery = [] } = useGetRestrictedLocationsQuery(
    {
      params: {
        inventoryOwner: {
          companyCustomerIdName: inventoryOwnerId,
          dataCompanyId,
        },
        locationId,
        originId: bookingOriginId,
        productId,
      },
    },
    { skip: R.isEmpty(productId) }
  );

  const {
    data: recipientsQuery = [],
    isFetching: isFetchingRecipients,
    isSuccess: isRecipientsSuccess,
  } = useGetRecipientsQuery(
    {
      params: {
        dataCompanyId,
      },
    },
    { skip: R.isEmpty(dataCompanyId) }
  );

  const recipientsOptions = recipientsQuery.map(
    ({ recipientIdName: companyCustomerIdName, recipientName: companyCustomerName }) => ({
      companyCustomerIdName,
      companyCustomerName,
      dataCompanyId,
    })
  );

  const [updatedTotalDistributedVol, setUpdatedTotalDistributedVol] = useState(totalDistributedVol);

  const formattedStrToInt = value => parseInt(R.replace(/,/g, '', value));
  const sumTotalDistributedVolume = value =>
    R.sum([formattedStrToInt(value), totalDistributedVol - volume]);

  const validationProp = R.prop(R.__, validationMessages);
  useEffect(() => {
    if (isLocationSuccess && !locationsQuery.some(({ id }) => id === editState.location.id)) {
      setValidationMessages(
        R.assoc(
          'location',
          'Only locations that can accept the product and have a valid tariff route from the origin can be selected'
        )
      );
    } else if (validationProp('location')) setValidationMessages(R.assoc('location', ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocationSuccess, editState.location.id]);

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.lte(R.__, 0), R.always(GT_ZERO)],
    [value => !isNumberWithOptionalFormatting(value), R.always(INTEGER)],
    [
      value => R.gt(sumTotalDistributedVolume(value), shipmentVolume),
      R.always(
        'Distribution Volume causes the total distributed volume to exceed the Shipment Volume'
      ),
    ],
    [R.T, R.always('')],
  ]);

  const sanitizeVolume = R.compose(R.replace(/[^0-9]/g, ''), R.path(['target', 'value']));

  const handleVolumeChange = R.compose(
    setEditState,
    R.assoc('volume'),
    formatNumberWithoutDecimals,
    R.tap(R.compose(setValidationMessages, R.assoc('volume'), validateVolume)),
    sanitizeVolume
  );

  const handleUpdateTotalDistributedVol = R.compose(
    setUpdatedTotalDistributedVol,
    sumTotalDistributedVolume,
    R.or(R.__, '0'),
    sanitizeVolume
  );

  const validateChainRecipients = R.compose(
    setValidationMessages,
    R.assoc('chainRecipients'),
    R.ifElse(R.isEmpty, R.always(REQUIRED), R.always(''))
  );

  const handleChainRecipientsChange = R.compose(
    setEditState,
    R.assoc('chainRecipients'),
    R.tap(validateChainRecipients)
  );

  return (
    <FormGroup sx={{ marginBottom: 5 }}>
      <Stack spacing={3} sx={{ mt: 3 }}>
        <DataDisplayItem
          key="Shipment Volume"
          label="Shipment Volume"
          value={`${formatNumberWithoutDecimals(shipmentVolume || 0)} bbl`}
        />
        <Stack spacing={0.5}>
          <Typography sx={{ alignItems: 'center', display: 'flex' }} variant="caption">
            Total Distributed Volume
            <Tooltip enterTouchDelay={10} title={totalDistributedVolumeTooltip}>
              <InfoRounded sx={{ color: 'graphic.main', ml: 1 }} />
            </Tooltip>
          </Typography>
          <Typography
            sx={{
              color:
                R.or(
                  R.lte(updatedTotalDistributedVol, 0),
                  R.gt(updatedTotalDistributedVol, shipmentVolume)
                ) && 'error.main',
            }}
            variant="body1bold"
          >
            {`${formatNumberWithoutDecimals(updatedTotalDistributedVol || 0)} bbl`}
          </Typography>
        </Stack>
      </Stack>
      <Divider sx={{ marginBottom: 2, marginTop: 3 }} />
      <Box sx={{ alignItems: 'center', display: 'flex', marginBottom: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={editState.reconsignmentControl}
              onChange={({ target: { checked } }) =>
                setEditState(R.assoc('reconsignmentControl', checked))
              }
            />
          }
          label={
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
              }}
            >
              <Typography>Set a reconsignment control</Typography>
            </Box>
          }
        />
        <Tooltip enterTouchDelay={10} title={reconsignmentTooltip}>
          <InfoRounded sx={{ color: 'graphic.main' }} />
        </Tooltip>
      </Box>
      {editState.reconsignmentControl && (
        <RestrictedLocationsList restrictedLocations={restrictedLocationsQuery} />
      )}
      <Autocomplete
        disableClearable
        disabled={
          !isLocationSuccess || (isLocationSuccess && R.isEmpty(locationsQuery)) || isFieldsDisabled
        }
        getOptionLabel={R.prop('locationName')}
        isOptionEqualToValue={R.eqProps('id')}
        loading={isFetchingLocations}
        onChange={(_, value) => {
          setEditState(R.assoc('location', value));
          if (validationProp('location')) setValidationMessages(R.assoc('location', ''));
        }}
        options={locationsQuery}
        renderInput={props => (
          <TextField
            {...props}
            error={R.not(R.isEmpty(validationProp('location')))}
            helperText={validationProp('location')}
            label="Location"
          />
        )}
        sx={{ marginBottom: 3 }}
        value={editState.location}
      />
      {R.and(isLocationSuccess, R.isEmpty(locationsQuery)) && (
        <Alert severity="warning" sx={{ marginBottom: 3 }}>
          There are no valid locations available for selection. Please contact the Batch Owner to
          update the origin or product on the parent batch.
        </Alert>
      )}
      <TextField
        disabled={isFieldsDisabled}
        error={R.not(R.isEmpty(validationProp('volume')))}
        helperText={validationProp('volume')}
        label="Volume"
        onChange={event => {
          handleVolumeChange(event);
          handleUpdateTotalDistributedVol(event);
        }}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
          },
        }}
        sx={{ marginBottom: 3 }}
        value={editState.volume}
      />
      <FormControlLabel
        control={
          <Switch
            checked={editState.shipmentChain}
            onChange={({ target: { checked } }) => {
              if (checked) setEditState(R.assoc('shipmentChain', checked));
              else {
                setEditState(R.mergeLeft({ chainRecipients: [], shipmentChain: checked }));
                if (validationProp('chainRecipients'))
                  setValidationMessages(R.assoc('chainRecipients', ''));
              }
            }}
          />
        }
        label={<Typography>Is a shipment chain required?</Typography>}
        sx={{ marginBottom: 3 }}
      />
      <Autocomplete
        disableClearable
        disabled={!editState.shipmentChain || !isRecipientsSuccess}
        getOptionLabel={R.prop('companyCustomerName')}
        isOptionEqualToValue={R.eqProps('companyCustomerIdName')}
        loading={isFetchingRecipients}
        multiple
        onChange={(_, values) => handleChainRecipientsChange(values)}
        options={recipientsOptions}
        renderInput={props => (
          <TextField
            {...props}
            error={R.not(R.isEmpty(validationProp('chainRecipients')))}
            helperText={validationProp('chainRecipients')}
            label="Recipient"
          />
        )}
        renderTags={(tags, getProps) =>
          tags?.map((option, index) => (
            <Chip
              {...getProps({ index })}
              label={option.companyCustomerName}
              sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
              variant="outlined"
            />
          ))
        }
        value={editState.chainRecipients}
      />
    </FormGroup>
  );
};

EditDistribution.propTypes = {
  distributionData: PropTypes.shape({
    bookingOriginId: PropTypes.number,
    dataCompanyId: PropTypes.number,
    inventoryOwnerId: PropTypes.string,
    locationId: PropTypes.number,
    productId: PropTypes.number,
    shipmentId: PropTypes.number,
    shipmentVolume: PropTypes.number,
    totalDistributedVol: PropTypes.number,
    volume: PropTypes.number,
  }).isRequired,
  editState: PropTypes.shape({
    chainRecipients: PropTypes.arrayOf(PropTypes.shape({})),
    location: PropTypes.shape({ id: PropTypes.number, locationName: PropTypes.string }),
    reconsignmentControl: PropTypes.bool.isRequired,
    shipmentChain: PropTypes.bool.isRequired,
    volume: PropTypes.string,
  }).isRequired,
  isDistributionSuccess: PropTypes.bool.isRequired,
  isFieldsDisabled: PropTypes.bool,
  setEditState: PropTypes.func.isRequired,
  setValidationMessages: PropTypes.func.isRequired,
  validationMessages: PropTypes.shape({
    chainRecipients: PropTypes.string,
    location: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
};
EditDistribution.defaultProps = {
  isFieldsDisabled: false,
};

export default EditDistribution;
